import React from 'react'
import { Box, Grid, Typography } from '@mui/material'

import styles from './index.module.css'

// PDF preview imports
import { Tooltip, IconButton } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
import {
  getArtworkRenderPreviewId,
  getArtworkPreviewSources,
  getPreparedRenderPreviewUrl,
  getRenderPreviewSourceType,
  resolveArtworkDisplayStrategy,
  resolveOriginalOnlyStrategy,
} from "../proofPreviewHelpers";

const TEXT_GREY = '#55656d'
const IMAGE_CDN = `https://api-asicentral.com/v1/`;

// pdf.js ESM worker (works with CRA/Vite)
GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

// Preview helpers
const getExt = (url = "") =>
  (url.match(/\.([a-z0-9]+)(?:\?|#|$)/i)?.[1] || "FILE").toUpperCase();

const getFileName = (s = "") => {
  try {
    const u = new URL(s, window.location.origin);
    return decodeURIComponent(u.pathname.split("/").pop() || "File");
  } catch {
    const parts = String(s).split(/[\/?#]/);
    return decodeURIComponent(parts.pop() || "File");
  }
};

const resolveSrc = (v) => (v?.startsWith("http") ? v : `${IMAGE_CDN}${v}?size=normal`);
const resolveRaw = (v) => (v?.startsWith("http") ? v : `${IMAGE_CDN}${v}`);

const pdfThumbCache = new Map();

const decodeImage = async (src) => {
  if (!src) return;

  const image = new Image();
  image.src = src;

  if (typeof image.decode === "function") {
    try {
      await image.decode();
      return;
    } catch {
      // Fall through to load/error listeners.
    }
  }

  if (image.complete) return;

  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
  });
};

const PdfThumb = ({
  src,
  width = 200,
  height = 200,
  skeleton = "Loading PDF preview...",
  fileName = "File",
  onStatusChange = null,
}) => {
  const [dataUrl, setDataUrl] = React.useState(() => pdfThumbCache.get(src) || "");
  const [status, setStatus] = React.useState(() => (pdfThumbCache.has(src) ? "ready" : "loading"));

  React.useEffect(() => {
    onStatusChange?.(status);
  }, [onStatusChange, status]);

  React.useEffect(() => {
    let cancelled = false;

    if (!src) {
      setDataUrl("");
      setStatus("failed");
      return () => {
        cancelled = true;
      };
    }

    const cached = pdfThumbCache.get(src) || "";
    setDataUrl(cached);
    setStatus(cached ? "ready" : "loading");
    if (cached) {
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        setStatus("loading");
        const loadingTask = getDocument({ url: src, withCredentials: false });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);

        const v0 = page.getViewport({ scale: 1 });
        const scale = Math.min(width / v0.width, height / v0.height);
        const viewport = page.getViewport({ scale: Math.max(scale, 0.2) });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        await page.render({ canvasContext: ctx, viewport }).promise;
        if (cancelled) return;

        const url = canvas.toDataURL("image/png");
        await decodeImage(url);
        if (cancelled) return;

        pdfThumbCache.set(src, url);
        setDataUrl(url);
        setStatus("ready");
      } catch {
        if (!cancelled) {
          setDataUrl("");
          setStatus("failed");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [src, width, height]);

  return (
    <div
      data-po-pdf-preview
      data-po-pdf-status={status}
      data-po-pdf-src={src || undefined}
      style={{ width, height, display: "grid", placeItems: "center" }}
    >
      {status === "ready" && dataUrl ? (
        <img
          src={dataUrl}
          alt="PDF preview"
          style={{ width, height, objectFit: "contain" }}
          loading="eager"
          decoding="async"
        />
      ) : status === "failed" ? (
        <Box
          sx={{
            textAlign: "center",
            px: 1,
            color: "text.secondary",
          }}
        >
          <Typography variant="caption">
            PDF preview unavailable
          </Typography>
          <Typography variant="caption" display="block">
            {fileName}
          </Typography>
        </Box>
      ) : (
        <div style={{ width, height, display: "grid", placeItems: "center", fontSize: 11, color: "#888" }}>
          {skeleton}
        </div>
      )}
    </div>
  );
};

const proofRequiredOptions = [
  { id: 0, name: 'None' },
  { id: 1, name: 'Email Proof' },
  { id: 2, name: 'Pre Production Sample' },
  { id: 3, name: 'Digital Photo' }
];
const repeatLogoOptions = [
  { id: 1, name: 'Yes' },
  { id: 2, name: 'No' }
];

const cleanNote = (note) => String(note || '').trim();

const ArtWorkDetails = ({ artwork, location, decorationSupplierName }) => {
  const { originalFileUrl, storedPreviewUrl } = getArtworkPreviewSources(artwork);
  const strategy = React.useMemo(() => resolveArtworkDisplayStrategy(artwork), [artwork]);
  const fallbackStrategy = React.useMemo(
    () => resolveOriginalOnlyStrategy(originalFileUrl),
    [originalFileUrl]
  );
  const [storedPreviewFailed, setStoredPreviewFailed] = React.useState(false);
  const [storedPreviewStatus, setStoredPreviewStatus] = React.useState("loading");
  React.useEffect(() => {
    setStoredPreviewFailed(false);
    setStoredPreviewStatus("loading");
  }, [storedPreviewUrl, originalFileUrl]);

  const effectiveStrategy = React.useMemo(() => {
    if (storedPreviewFailed && strategy.type === "stored-preview") {
      return fallbackStrategy;
    }
    return strategy;
  }, [fallbackStrategy, storedPreviewFailed, strategy]);
  React.useEffect(() => {
    if (
      storedPreviewFailed &&
      strategy.type === "stored-preview" &&
      fallbackStrategy.type === "file-fallback"
    ) {
      setStoredPreviewStatus("failed");
    }
  }, [fallbackStrategy.type, storedPreviewFailed, strategy.type]);
  const hasStoredPreviewStrategy = strategy.type === "stored-preview";
  const rawPath = originalFileUrl || "";
  const raw = rawPath ? resolveRaw(rawPath) : "";
  const renderPreviewId = React.useMemo(() => getArtworkRenderPreviewId(artwork), [artwork]);
  const preparedRenderPreviewUrl = React.useMemo(
    () => getPreparedRenderPreviewUrl(artwork),
    [artwork]
  );
  // The PDF renderer only injects prepared assets for validated PDF/EPS sources.
  // For legacy artwork, the original URL may not have a .pdf extension even
  // though the server has verified its content type. Trust that scoped asset.
  const displayPreviewUrl =
    preparedRenderPreviewUrl ||
    (hasStoredPreviewStrategy && strategy.displayUrl
      ? resolveRaw(strategy.displayUrl)
      : "");
  const thumb =
    effectiveStrategy.type === "original-image" && effectiveStrategy.displayUrl
      ? resolveSrc(effectiveStrategy.displayUrl)
      : "";
  const originalIsPdfLike = React.useMemo(
    () => ["pdf", "eps"].includes(getRenderPreviewSourceType(artwork)),
    [artwork]
  );
  const markerDisplayUrl =
    displayPreviewUrl ||
    (!originalIsPdfLike && effectiveStrategy.type === "original-image" ? thumb : "");
  const renderPreviewSourceType = React.useMemo(
    () => effectiveStrategy.renderPreviewSourceType || getRenderPreviewSourceType(artwork),
    [artwork, effectiveStrategy.renderPreviewSourceType]
  );
  const usesRenderPreviewMarker =
    Boolean(renderPreviewId) &&
    (Boolean(preparedRenderPreviewUrl) ||
      (originalIsPdfLike && Boolean(renderPreviewSourceType)));
  const ext = rawPath ? getExt(rawPath) : "";
  const fileName = getFileName(rawPath || displayPreviewUrl || "");

  const proofRequiredText = proofRequiredOptions.find(
    o => o.id === Number(artwork?.ProofRequired)
  )?.name || '-';
  const repeatLogoText = repeatLogoOptions.find(
    o => o.id === Number(artwork?.RepeatLogo)
  )?.name || '-';
  const decoratorNote = cleanNote(artwork?.SupplierNotes ?? artwork?.supplierNotes);

  return (
    <>
      <Grid container className={styles.footerContainer}>
        <Grid className={styles.Artworks}>
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant='body2'>Proof Required</Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant='body2'>{proofRequiredText || '-'}</Typography>
          </Box>
          {decorationSupplierName && (
            <Box className={styles.container}>
              <Typography className={styles.title} color={TEXT_GREY} variant='body2'>Decoration Supplier</Typography>
              <Typography className={styles.value} color={TEXT_GREY} variant='body2'>{decorationSupplierName}</Typography>
            </Box>
          )}
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant='body2'>Logo Name</Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant='body2' sx={{ whiteSpace: "pre-line" }}>{artwork?.LogoName || '-'}</Typography>
          </Box>
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant='body2'>Repeat Logo</Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant='body2'>{repeatLogoText || '-'}</Typography>
          </Box>
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant='body2'>Logo Location</Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant='body2'>{location || '-'}</Typography>
          </Box>
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant='body2'>Logo Size</Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant='body2'>{artwork?.LogoSize || '-'}</Typography>
          </Box>
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant='body2'>Color of Logo</Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant='body2'>{artwork?.LogoColor || '-'}</Typography>
          </Box>
          <Box className={styles.container}>
            <Typography className={styles.title} color={TEXT_GREY} variant='body2'>Imprint Type</Typography>
            <Typography className={styles.value} color={TEXT_GREY} variant='body2'>{artwork?.ImprintType || '-'}</Typography>
          </Box>
          {decoratorNote && (
            <Box className={styles.notesContainer}>
              <Typography className={styles.title} color={TEXT_GREY} variant='body2'>Decorator Notes</Typography>
              <Typography className={`${styles.value} ${styles.notesValue}`} color={TEXT_GREY} variant='body2' sx={{ whiteSpace: "pre-line" }}>
                {decoratorNote}
              </Typography>
            </Box>
          )}
          {/* {type === "Different_supplier_decoration" ? (
            <Grid>
              <Box className={styles.container}>
                <Typography className={styles.title} color={TEXT_GREY} variant='body2'>Supplier Name</Typography>
                <Typography className={styles.value} color={TEXT_GREY} variant='body2'>{dummySupplierData?.supplierName || '-'}</Typography>
              </Box>
              <Box className={styles.container}>
                <Typography className={styles.title} color={TEXT_GREY} variant='body2'>Supplier Logo</Typography>
                <Typography className={styles.value} color={TEXT_GREY} variant='body2'>{dummySupplierData?.LogoName || '-'}</Typography>
              </Box>
              <Box className={styles.container}>
                <Typography className={styles.title} color={TEXT_GREY} variant='body2'>Supplier Imprint Type</Typography>
                <Typography className={styles.value} color={TEXT_GREY} variant='body2'>{dummySupplierData?.ImprintType || '-'}</Typography>
              </Box>
            </Grid>
          ) : ""} */}
        </Grid>

        {(originalFileUrl || displayPreviewUrl) && (
          <Grid className={styles.totalPriceContainer}>
            <Tooltip title={`${fileName} - ${ext}`}>
              <Box
                className={styles.primaryImageContainer}
                sx={{
                  border: "1px dashed #bdbdbd",
                  borderRadius: "8px",
                  width: 200,
                  height: 200,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "#fafafa",
                  overflow: "hidden",
                  position: "relative",
                  cursor: raw ? "pointer" : "default",
                }}
                onClick={() => {
                  if (raw) {
                    window.open(raw, "_blank", "noopener,noreferrer");
                  }
                }}
              >
                {raw && (
                  <IconButton
                    size="small"
                    aria-label="Preview file"
                    title="Preview"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(raw, "_blank", "noopener,noreferrer");
                    }}
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      zIndex: 2,
                      bgcolor: "white",
                      boxShadow: 1,
                      "&:hover": { bgcolor: "white" },
                    }}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                )}

                {usesRenderPreviewMarker ? (
                  <Box
                    sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center" }}
                    data-po-render-preview=""
                    data-po-render-preview-id={renderPreviewId}
                    data-po-render-preview-type={renderPreviewSourceType || (preparedRenderPreviewUrl ? "pdf" : "")}
                    data-po-render-preview-status={markerDisplayUrl ? storedPreviewStatus : "failed"}
                    data-po-render-preview-src={markerDisplayUrl || raw || undefined}
                  >
                    {markerDisplayUrl && !storedPreviewFailed ? (
                      <img
                        src={markerDisplayUrl}
                        alt={artwork?.LogoName || "PDF artwork preview"}
                        className={styles.primaryImage}
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        loading="eager"
                        decoding="async"
                        onLoad={(event) => {
                          const image = event.currentTarget;
                          setStoredPreviewStatus(
                            image.complete && image.naturalWidth > 0 && image.naturalHeight > 0
                              ? "ready"
                              : "failed"
                          );
                        }}
                        onError={() => {
                          setStoredPreviewStatus(
                            fallbackStrategy.type === "file-fallback" ? "failed" : "loading"
                          );
                          setStoredPreviewFailed(true);
                        }}
                      />
                    ) : effectiveStrategy.type === "original-image" ? (
                      <img
                        src={thumb}
                        alt={artwork?.LogoName || 'Artwork preview'}
                        className={styles.primaryImage}
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        loading="eager"
                        decoding="async"
                        onLoad={(event) => {
                          const image = event.currentTarget;
                          setStoredPreviewStatus(
                            image.complete && image.naturalWidth > 0 && image.naturalHeight > 0
                              ? "ready"
                              : "failed"
                          );
                        }}
                        onError={() => {
                          setStoredPreviewStatus("failed");
                        }}
                      />
                    ) : (
                      <Box sx={{ textAlign: "center", px: 1, pointerEvents: "none" }}>
                        <Typography variant="caption" color="text.secondary">
                          Preview unavailable
                        </Typography>
                        <Typography variant="caption" display="block">
                          {fileName}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : hasStoredPreviewStrategy ? (
                  <img
                    src={displayPreviewUrl}
                    alt={artwork?.LogoName || "PDF artwork preview"}
                    className={styles.primaryImage}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    loading="eager"
                    decoding="async"
                    onError={() => {
                      setStoredPreviewStatus("failed");
                      setStoredPreviewFailed(true);
                    }}
                  />
                ) : effectiveStrategy.type === "original-image" ? (
                  <img
                    src={thumb}
                    alt={artwork?.LogoName || 'Artwork preview'}
                    className={styles.primaryImage}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    loading="eager"
                    decoding="async"
                  />
                ) : effectiveStrategy.type === "legacy-pdf" ? (
                  <PdfThumb
                    src={raw}
                    width={200}
                    height={200}
                    fileName={fileName}
                  />
                ) : (
                  <Box sx={{ textAlign: "center", px: 1, pointerEvents: "none" }}>
                    <Typography variant="caption" color="text.secondary">
                      Preview unavailable
                    </Typography>
                    <Typography variant="caption" display="block">
                      {fileName}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Tooltip>

            {raw && (
              <Typography
                variant="caption"
                component="div"
                sx={{ mt: 1, textAlign: 'center' }}
              >
                <a
                  href={raw}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {effectiveStrategy.type === "legacy-pdf" ? 'View original PDF' : 'Click to enlarge'}
                </a>
              </Typography>
            )}
          </Grid>
        )}
      </Grid>
    </>

  )
}

export default ArtWorkDetails
